/**
 * Altura del viewport medida por JS → `--vp-h` en `<html>` (px).
 *
 * Por qué no alcanza con `100dvh`: en la PWA instalada de Android (medido en un
 * Galaxy S23, Chrome 152) un pull-to-refresh recarga la página mientras el
 * viewport pasa por un valor transitorio más alto (910 con una pantalla de 854).
 * Chrome acomoda el frame con ese `100dvh` y después NO lo re-evalúa: una sonda
 * nueva con `height:100dvh` mide 854, pero `.app-shell__frame` sigue en 910 →
 * las cards del home crecen y el tab bar cae bajo el fold. `innerHeight` en ese
 * mismo estado ya dice 854, así que la medida JS es la fiable. Un valor en px
 * además fuerza el recálculo que la unidad no dispara.
 *
 * El CSS usa `var(--vp-h, 100dvh)`: sin JS (SSR, tests) cae al comportamiento
 * anterior. iOS standalone sigue con su override `100lvh` (app-shell.css), que
 * viene después y pisa este valor — ahí `innerHeight` es el viewport chico.
 *
 * Teclado: `innerHeight` sólo se achica con el teclado si el layout viewport se
 * achica, y en Chrome Android (108+) el default es NO hacerlo — encoge el
 * visual viewport y panea la página, con lo que el header y los primeros
 * mensajes de la charla quedan arriba del borde visible. Por eso `index.html`
 * declara `interactive-widget=resizes-content`: con eso el teclado sí encoge
 * el layout viewport, dispara `resize`, y esta medida hace que el frame se
 * reacomode en el espacio que queda. iOS ignora esa meta y `innerHeight` no
 * cambia ahí, así que en iOS el comportamiento sigue siendo el de siempre. No
 * usar `visualViewport.height` como fuente: donde la meta no aplica, el
 * visual viewport se encoge Y se panea (`offsetTop` > 0), y un frame más
 * corto sin corregir ese pan queda medio fuera de pantalla — peor que hoy.
 */
export function installViewportHeightVar(): () => void {
  const root = document.documentElement;
  let raf = 0;
  const apply = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      const h = window.innerHeight;
      if (h > 0) root.style.setProperty('--vp-h', `${h}px`);
    });
  };
  apply();
  // El valor transitorio de la recarga puede asentarse después del primer
  // frame sin disparar `resize` — re-medimos un par de veces al arrancar.
  const timers = [300, 1000, 2500].map((ms) => window.setTimeout(apply, ms));
  window.addEventListener('resize', apply);
  window.addEventListener('orientationchange', apply);
  window.addEventListener('pageshow', apply);
  document.addEventListener('visibilitychange', apply);
  window.visualViewport?.addEventListener('resize', apply);
  return () => {
    cancelAnimationFrame(raf);
    timers.forEach((t) => window.clearTimeout(t));
    window.removeEventListener('resize', apply);
    window.removeEventListener('orientationchange', apply);
    window.removeEventListener('pageshow', apply);
    document.removeEventListener('visibilitychange', apply);
    window.visualViewport?.removeEventListener('resize', apply);
    root.style.removeProperty('--vp-h');
  };
}
